import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { Form, Divider } from "antd";
import { useDispatch } from "react-redux";

import { RAButtonPrimary, RAButtonSecondary, RAImage, RATextInput, RATypography } from "@/components/customs";
import { fetchUserLogin } from "@/stores/slices/authSlice";
import { REGEX_EMAIL } from "@/config/constants";
import { HeaderSection } from "../../Cards";
import { PATH_ROUTES } from "@/config/routes";
import { noop } from "lodash";

const initFormData = {
	email: "",
	password: "",
};

const LoginComponent = ({ changeRoute, openAuthTitle, onFinish = noop }) => {
	const dispatch = useDispatch();
	const router = useRouter();
	const formRef = useRef();

	const [errors, setErrors] = useState("");
	const [formData, setFormData] = useState(initFormData);
	const [loading, setLoading] = useState(false);

	const onSubmitFormLogin = async () => {
		const { email, password } = formData;
		let errs = {};
		if (!email) {
			errs.email = "Please enter your email.";
		}
		if (!REGEX_EMAIL.test(email)) {
			errs.email = "Please enter valid email.";
		}
		if (!password) {
			errs.password = "Please enter your password.";
		}

		if (!_.isEmpty(errs)) {
			setErrors(errs);
			return;
		}

		if (loading) return;
		setLoading(true);

		const response = await dispatch(fetchUserLogin({ ...formData, forceCloseModal: true }));
		if (response && response?.payload?.success) {
			onFinish();
			if (router.query?.returnUrl) {
				router.push(decodeURIComponent(router.query?.returnUrl));
			} else if (!openAuthTitle || openAuthTitle === "Log In") {
				router.push(PATH_ROUTES.PATH_ACCOUNT_DASHBOARD);
			}
		}

		setTimeout(() => {
			setLoading(false);
		}, 500);
	};

	const onPressForgotPassword = () => {
		changeRoute?.("RESET");
	};

	const onPressRegister = () => {
		changeRoute?.("REGISTER");
	};

	function handleKeyUp(event) {
		if (event.keyCode === 13) {
			onSubmitFormLogin();
		}
	}

	return (
		<LoginInformationSection>
			<SignInBg>
				<RAImage
					width="100%"
					height="100%"
					url="/images/sign-in-bg.png"
					alt="sign-in-bg"
					borderRadius="24px 0 0 24px"
					objectPosition="33% center"
				/>
				<div
					style={{
						position: "absolute",
						bottom: 48,
						left: 48,
						right: 48,
					}}
				>
					<RATypography variant="h6" align="center">
						Don’t have an account yet?
					</RATypography>
					<RATypography variant="p" top={10} bottom={20} color="#fff" align="center">
						Create your Royal Air account to submit requests and access available empty legs and alerts.
					</RATypography>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<RAButtonPrimary title="Sign-up now" textColor="#fff" onClick={onPressRegister} />
					</div>
				</div>
			</SignInBg>
			<FormInputWrapper>
				<Form layout="vertical" className="form-login-wrapper" ref={formRef} onKeyUp={handleKeyUp}>
					<HeaderSection title={openAuthTitle || "Log In"} />
					<Divider style={{ margin: "16px 0" }} />
					<Form.Item>
						<RATextInput
							data-testid="Login_emailInput"
							title="Email"
							placeholder="Enter your email"
							onChange={(val) => {
								setFormData({ ...formData, email: val });
								setErrors({});
							}}
							error={errors?.email}
						/>
					</Form.Item>
					<Form.Item>
						<RATextInput
							data-testid="Login_passwordInput"
							title="Password"
							placeholder="Enter your password"
							type="password"
							onChange={(val) => {
								setFormData({ ...formData, password: val });
								setErrors({});
							}}
							error={errors?.password}
						/>
						<ForgotPasswordSection
							data-testid="Login_forgotPasswordLink"
							onClick={() => onPressForgotPassword()}
						>
							<ForgotPasswordText>Forgot Password?</ForgotPasswordText>
						</ForgotPasswordSection>
					</Form.Item>
					<WrapperBottom justify="center">
						<LoginButton>
							<RAButtonPrimary
								data-testid="Login_submitBtn"
								title="LOG IN"
								width="100%"
								onClick={onSubmitFormLogin}
								loading={loading}
							/>
						</LoginButton>
						<SignUpWrapper>
							<RATypography variant="p">No account yet?</RATypography>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									maxWidth: "170px",
									width: "100%",
								}}
							>
								<RAButtonSecondary
									width="100%"
									title="Sign-up now"
									size="small"
									onClick={onPressRegister}
								/>
							</div>
						</SignUpWrapper>
					</WrapperBottom>
				</Form>
			</FormInputWrapper>
		</LoginInformationSection>
	);
};

export default LoginComponent;

const LoginInformationSection = styled.div`
	width: 900px;
	display: flex;

	@media (max-width: 991px) {
		width: calc(100vw - 48px);
		max-width: 600px;
	}
`;

const SignInBg = styled.div`
	width: 50%;
	position: relative;

	@media (max-width: 991px) {
		display: none;
	}
`;
const FormInputWrapper = styled.div`
	width: 50%;
	padding: 100px 40px;

	@media (max-width: 991px) {
		width: 100%;
		padding: 24px;
	}
`;

const ForgotPasswordSection = styled.div`
	display: flex;
	align-items: center;
	background-color: Transparent;
	background-repeat: no-repeat;
	cursor: pointer;
	overflow: hidden;
	outline: none;
	border: none;
	margin-top: 16px;
`;

const ForgotPasswordText = styled.div`
	font-family: "DM Sans";
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 18px;
	display: flex;
	align-items: center;
	color: #222222;
`;

const SignUpWrapper = styled.div`
	width: 100%;
	align-items: center;
	gap: 8;
	justify-content: space-between;
	display: none;

	@media (max-width: 991px) {
		display: flex;
	}
`;

const WrapperBottom = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-top: 40px;
	gap: 24px;

	@media (max-width: 991px) {
		margin-top: 20px;
	}
`;

const LoginButton = styled.div`
	display: flex;
	width: 180px;

	@media (max-width: 991px) {
		width: 100%;
	}
`;
